'use client'

import { ReactLenis } from 'lenis/react'
import Link from 'next/link'
import { motion, type Variants } from 'framer-motion'
import { ArrowRight, Heart, Shield, Zap, Globe } from 'lucide-react'
import { GeometricBackground } from '@/components/ui/geometric-background'
import { MarketingNavbar } from '@/components/layout/marketing-navbar'
import { MarketingFooter } from '@/components/layout/marketing-footer'

const itemVariants: Variants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } }
}

export default function AboutClient() {
    return (
        <ReactLenis root>
            <div className="relative min-h-screen bg-black selection:bg-primary/30">
            <GeometricBackground />

            <MarketingNavbar />

            <main id="main-content">
                {/* Hero */}
                <section className="relative min-h-[60vh] flex flex-col justify-center pt-32 pb-20">
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(0,102,255,0.08),transparent_60%)] pointer-events-none" />
                    <div className="max-w-[1400px] 2xl:max-w-[1800px] mx-auto px-6 lg:px-12 relative z-10">
                        <motion.div
                            initial="hidden"
                            animate="visible"
                            variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
                            className="max-w-3xl"
                        >
                            <motion.span variants={itemVariants} className="text-primary/80 text-xs font-semibold tracking-[0.25em] uppercase">
                                About Lenklyst
                            </motion.span>
                            <motion.h1 variants={itemVariants} className="text-5xl sm:text-6xl lg:text-8xl 2xl:text-9xl font-bold tracking-tighter text-white mt-4 mb-6 leading-none uppercase">
                                Built for<br /><span className="text-gray-600">creators.</span><br />Owned by you.
                            </motion.h1>
                            <motion.p variants={itemVariants} className="text-white/50 text-lg leading-relaxed max-w-2xl">
                                We believe your links, your audience, and your data should belong to you — not a platform.
                                Lenklyst is a full-stack growth toolkit that puts powerful analytics, beautiful bio pages,
                                and trackable short links in your hands, with professional-grade features for everyone.
                            </motion.p>
                        </motion.div>
                    </div>
                </section>

                {/* Values - Redesigned Narrative Flow */}
                <section className="py-24 relative z-10">
                    <div className="absolute top-[20%] right-[-10%] w-[50vw] h-[50vw] bg-[radial-gradient(circle_at_center,rgba(0,102,255,0.04),transparent_70%)] blur-[120px] pointer-events-none" />
                    <div className="max-w-[1400px] 2xl:max-w-[1800px] mx-auto px-6 lg:px-12">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="mb-16 md:mb-32 text-center max-w-2xl mx-auto"
                        >
                            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tighter text-white mb-6 uppercase">
                                Our values.
                            </h2>
                            <div className="w-20 h-1 bg-primary/40 mx-auto rounded-full mb-6" />
                            <p className="text-white/40 text-xl font-light leading-relaxed">
                                Everything we build is rooted in these core principles, shaping a better future for creators.
                            </p>
                        </motion.div>

                        <div className="space-y-0">
                            {[
                                {
                                    icon: Shield,
                                    title: 'Privacy First',
                                    desc: 'Your data is yours. We never sell it, share it, or monetize it beyond providing the toolkit you need to grow.',
                                    number: '01'
                                },
                                {
                                    icon: Heart,
                                    title: 'Creator-Owned',
                                    desc: 'Designed from the ground up for people who create content, build brands, and grow genuine communities.',
                                    number: '02'
                                },
                                {
                                    icon: Zap,
                                    title: 'Always Improving',
                                    desc: 'We ship continuously based on real user feedback. You shape the roadmap, we execute the vision with precision.',
                                    number: '03'
                                },
                                {
                                    icon: Globe,
                                    title: 'No Hidden Costs',
                                    desc: 'A generous free tier, clear pricing, and no surprises. What you see is exactly what you get, every single time.',
                                    number: '04'
                                },
                            ].map((v, i) => (
                                <motion.div
                                    key={v.title}
                                    initial={{ opacity: 0 }}
                                    whileInView={{ opacity: 1 }}
                                    viewport={{ once: true, margin: '-100px' }}
                                    transition={{ duration: 0.8, ease: 'easeOut' }}
                                    className={`min-h-[50vh] md:min-h-screen flex flex-col ${i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-6 lg:gap-24 sticky top-0 bg-black px-6 lg:px-12`}
                                >
                                    <div className="flex-1 w-full">
                                        <div className="relative group">
                                            <div className="absolute -inset-4 bg-primary/5 rounded-[40px] blur-2xl group-hover:bg-primary/10 transition-colors" />
                                            <div className="relative aspect-[16/9] lg:aspect-square max-h-[400px] w-full rounded-3xl border border-white/5 bg-white/[0.02] flex items-center justify-center overflow-hidden">
                                                <div className="absolute top-6 left-6 text-6xl lg:text-8xl font-black text-white/[0.03] select-none tracking-tighter">
                                                    {v.number}
                                                </div>
                                                <div className="w-24 h-24 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center relative z-10 group-hover:scale-110 transition-transform duration-500">
                                                    <v.icon className="w-10 h-10 text-primary" />
                                                </div>
                                                <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className={`flex-1 text-center ${i % 2 === 0 ? 'lg:text-left' : 'lg:text-right'} space-y-6`}>
                                        <span className="text-primary/60 text-sm font-bold tracking-widest uppercase">Value {v.number}</span>
                                        <h3 className="text-4xl lg:text-6xl font-bold text-white tracking-tighter">{v.title}</h3>
                                        <p className="text-white/40 text-lg lg:text-xl leading-relaxed font-light max-w-xl mx-auto lg:mx-0">
                                            {v.desc}
                                        </p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Stats - Fluid Horizontal Flow */}
                <section className="py-32 border-y border-white/5 relative z-10 bg-white/[0.01]">
                    <div className="max-w-[1400px] 2xl:max-w-[1800px] mx-auto px-6 lg:px-12">
                        <div className="flex flex-wrap items-center justify-center lg:justify-between gap-y-16 gap-x-8">
                            {[
                                { value: '100%', label: 'Open & Transparent' },
                                { value: '∞', label: 'Unlimited on Premium' },
                                { value: '17+', label: 'Premium Themes' },
                                { value: '5+', label: 'Analytics Dimensions' },
                            ].map((stat, i) => (
                                <motion.div
                                    key={stat.label}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.1, duration: 0.6 }}
                                    className="text-center group"
                                >
                                    <span className="block text-6xl lg:text-8xl font-black text-white tracking-tighter group-hover:text-primary transition-colors duration-500 relative">
                                        {stat.value}
                                        <span className="absolute -inset-4 bg-primary/20 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </span>
                                    <p className="text-white/30 font-bold text-xs lg:text-sm mt-4 uppercase tracking-[0.2em]">{stat.label}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Mission Statement */}
                <section className="py-24 border-t border-white/5 relative z-10 overflow-hidden">
                    <div className="absolute left-[-10%] top-0 w-[50vw] h-[50vw] bg-[radial-gradient(circle_at_center,rgba(0,102,255,0.06),transparent_70%)] blur-[120px] pointer-events-none" />
                    <div className="max-w-[1400px] 2xl:max-w-[1800px] mx-auto px-6 lg:px-12">
                        <motion.div
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-100px' }}
                            className="sharp-edge rounded-3xl border border-white/[0.06] bg-white/[0.02] p-12 lg:p-20 relative overflow-hidden"
                        >
                            <div className="absolute top-0 right-0 w-64 h-64 bg-[radial-gradient(circle_at_center,rgba(0,102,255,0.08),transparent_70%)] blur-[60px] pointer-events-none" />
                            <span className="text-primary/80 text-xs font-semibold tracking-[0.25em] uppercase">Our Mission</span>
                            <blockquote className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white mt-6 leading-tight max-w-4xl">
                                "To give every creator, marketer, and builder the tools to grow their brand — without sacrificing their data or their budget."
                            </blockquote>
                            <p className="text-white/40 text-base mt-8 max-w-xl leading-relaxed">
                                We started Lenklyst because existing tools were either too expensive, too complex, or too extractive.
                                We wanted something that felt like it was built for the individual, empowering them with the tools to succeed at any scale.
                            </p>
                        </motion.div>
                    </div>
                </section>

                {/* CTA */}
                <section className="py-24 border-t border-white/5 relative z-10 overflow-hidden">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,102,255,0.06),transparent_70%)] blur-[150px] pointer-events-none" />
                    <div className="max-w-[1400px] 2xl:max-w-[1800px] mx-auto px-6 lg:px-12 text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="space-y-8"
                        >
                            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tighter text-white uppercase">
                                Ready to<br /><span className="text-primary">get started?</span>
                            </h2>
                            <p className="text-xl text-white/40 max-w-xl mx-auto font-light">
                                Join Lenklyst for free. No credit card, no lock-in.
                            </p>
                            <Link
                                id="about-cta-btn"
                                href="/auth/sign-up"
                                className="inline-flex items-center gap-3 px-10 py-5 bg-primary text-white rounded-full font-bold text-lg hover:bg-primary/90 transition-all hover:scale-105 active:scale-95 shadow-[0_0_60px_rgba(0,102,255,0.4)]"
                            >
                                Start for Free
                                <ArrowRight className="w-5 h-5" />
                            </Link>
                        </motion.div>
                    </div>
                </section>
            </main>

            <MarketingFooter />
        </div>
        </ReactLenis>
    )
}
